import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert } from '@mui/material';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { apiGet } from '../../../utils/fetch';
import {
    SystemNotificationConversationByType,
    SystemNotificationConversationByRecipient,
    SystemNotificationConversationByFromUser,
    GenericResponseInterface
} from '../../../interfaces/interfaces';

const SystemNotificationsAdminGetSystemNotificationData: React.FC = () => {
    const [recipientUserId, setRecipientUserId] = useState('');
    const [fromUserId, setFromUserId] = useState('');
    const [notificationType, setNotificationType] = useState('');
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [rawDialogOpen, setRawDialogOpen] = useState(false);

    const handleGetByRecipientUserId = async () => {
        setError(null);
        setData(null);
        if (!recipientUserId) return setError('Please enter a Recipient User ID');
        try {
            const result = await apiGet<GenericResponseInterface<SystemNotificationConversationByRecipient[]>>(`/systemNotifications/byRecipientUserId/${recipientUserId}`);
            if (result.status && result.data) {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to fetch by Recipient User ID');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch by Recipient User ID');
        }
    };

    const handleGetByFromUserId = async () => {
        setError(null);
        setData(null);
        if (!fromUserId) return setError('Please enter a From User ID');
        try {
            const result = await apiGet<GenericResponseInterface<SystemNotificationConversationByFromUser[]>>(`/systemNotifications/byForUserId/${fromUserId}`);
            if (result.status && result.data) {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to fetch by From User ID');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch by From User ID');
        }
    };

    const handleGetByNotificationType = async () => {
        setError(null);
        setData(null);
        if (!notificationType) return setError('Please enter a Notification Type');
        try {
            const result = await apiGet<GenericResponseInterface<SystemNotificationConversationByType[]>>(`/systemNotifications/byNotificationType/${notificationType}`);
            if (result.status && result.data) {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to fetch by Notification Type');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch by Notification Type');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleGetByRecipientUserId}>By Recipient User ID</Button>
                <TextField
                    label="Recipient User ID"
                    value={recipientUserId}
                    onChange={e => setRecipientUserId(e.target.value)}
                    size="small"
                />
                <Button variant="contained" onClick={handleGetByFromUserId}>By From User ID</Button>
                <TextField
                    label="From User ID"
                    value={fromUserId}
                    onChange={e => setFromUserId(e.target.value)}
                    size="small"
                />
                <Button variant="contained" onClick={handleGetByNotificationType}>By Notification Type</Button>
                <TextField
                    label="Notification Type"
                    value={notificationType}
                    onChange={e => setNotificationType(e.target.value)}
                    size="small"
                />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {data && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">System Notification Data (Tree View)</Typography>
                        <Button variant="outlined" size="small" onClick={() => setRawDialogOpen(true)}>
                            View Raw JSON
                        </Button>
                    </Box>
                    <JsonView
                        data={data}
                        shouldExpandNode={(level) => level < 2}
                        style={defaultStyles}
                    />
                </Paper>
            )}
            <Dialog open={rawDialogOpen} onClose={() => setRawDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Raw JSON</DialogTitle>
                <DialogContent>
                    <pre style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(data, null, 2)}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRawDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemNotificationsAdminGetSystemNotificationData;